import { useEffect, useRef } from 'react';

export const getLastPage = (total?: number, pageSize?: number): number =>
  Math.max(1, Math.ceil((total ?? 0) / (pageSize || 10)));

interface UsePageOverflowParams {
  current?: number;
  pageSize?: number;
  total?: number;
  disabled?: boolean;
  onClamp: (lastPage: number) => void;
}

interface UsePageOverflowResult {
  lastPage: number;
  isOverflow: boolean;
}

/** Moves to the last available page when `current` no longer exists, at most once per page/size/total. */
export const usePageOverflow = ({
  current,
  pageSize,
  total,
  disabled,
  onClamp,
}: UsePageOverflowParams): UsePageOverflowResult => {
  const lastPage = getLastPage(total, pageSize);
  // `total` is undefined until the first response lands.
  const isOverflow = !disabled && typeof total === 'number' && typeof current === 'number' && current > lastPage;

  const clampedKey = useRef<string | null>(null);
  const onClampRef = useRef(onClamp);
  onClampRef.current = onClamp;

  useEffect(() => {
    if (!isOverflow) {
      clampedKey.current = null;
      return;
    }

    const key = `${current}|${pageSize}|${total}`;
    if (clampedKey.current === key) {
      return;
    }

    clampedKey.current = key;
    onClampRef.current(lastPage);
  }, [isOverflow, current, pageSize, total, lastPage]);

  return { lastPage, isOverflow };
};
